import { List, Typography, Pagination, Avatar, Tag, Switch, Checkbox, Input, Select, message } from 'antd';
import { useProductsList, getProductsQueryKey } from 'hooks/api';
import { EditFilled, CopyOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { changeProductForFeed } from 'services/product.service';
import APP_ROUTES from 'config/routes';
import { usePagination } from 'hooks/common';

import { Link, useSearchParams } from 'react-router-dom';
import styles from './index.module.scss';

const { Text } = Typography;

const PAGE_SIZE = 20;

const ORDER_OPTIONS = [
  { value: '', label: 'No sorting' },
  { value: 'asc', label: 'Ascending' },
  { value: 'desc', label: 'Descending' },
];

const ProductList = () => {
  const { page, setPage } = usePagination(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('name') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get('name') || '');
  const [priceOrder, setPriceOrder] = useState<'' | 'asc' | 'desc'>(
    () => (searchParams.get('priceOrder') as '' | 'asc' | 'desc') || '',
  );
  const [forFeedFilter, setForFeedFilter] = useState(() => searchParams.get('forFeed') === 'true');
  const [inStockFilter, setInStockFilter] = useState(() => searchParams.get('inStock') === 'true');
  const isFirstRender = useRef(true);
  const client = useQueryClient();

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const params: Record<string, string> = { page: String(page) };
    if (debouncedSearch) params.name = debouncedSearch;
    if (priceOrder) params.priceOrder = priceOrder;
    if (forFeedFilter) params.forFeed = 'true';
    if (inStockFilter) params.inStock = 'true';
    setSearchParams(params, { replace: true });
  }, [page, debouncedSearch, priceOrder, forFeedFilter, inStockFilter]);

  const { data, isLoading } = useProductsList({
    skip: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
    name: debouncedSearch || undefined,
    priceOrder: priceOrder || undefined,
    forFeed: forFeedFilter || undefined,
    inStock: inStockFilter || undefined,
  });

  const { mutateAsync: changeForFeed } = useMutation(changeProductForFeed);

  const handleChangeForFeed = useCallback((id: string, forFeed: boolean) => {
    changeForFeed({ id, forFeed })
      .then(() => client.invalidateQueries(getProductsQueryKey))
      .catch((err: any) => message.error(err?.response?.data?.message || err?.message || 'Failed to update product'));
  }, [changeForFeed, client]);

  const handlePriceOrderChange = useCallback((value: '' | 'asc' | 'desc') => {
    setPriceOrder(value);
    setPage(1);
  }, [setPage]);

  const handleForFeedFilterChange = useCallback((checked: boolean) => {
    setForFeedFilter(checked);
    setPage(1);
  }, [setPage]);

  const handleInStockFilterChange = useCallback((checked: boolean) => {
    setInStockFilter(checked);
    setPage(1);
  }, [setPage]);

  const renderItem = useCallback((item: Product.Item, idx) => {
    const descriptionRender = (
      <div className={styles.description}>
        <div className={styles.store}>
          {item.store.map((s, storeIndex) => (
            <div key={storeIndex.toString()}>
              <Switch defaultChecked />
              <span className={styles.name}>{s}</span>
            </div>
          ))}
        </div>
        <div>
          {item.values[0]?.display && (
            <Tag
              className={styles.displayTag}
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(item.values[0].display);
                message.success('Скопировано!');
              }}
            >
              {item.values[0].display}
            </Tag>
          )}
          {item.values.map((i, valueIdx) => (
            <span key={valueIdx.toString()}>
              <Tag className={styles.tag}>
                $
                {' '}
                {i.price / 100}
              </Tag>
              {i.outOfStock && (
                <Tag color='red'>Out of stock</Tag>
              )}
            </span>
          ))}
        </div>
      </div>
    );

    const titleRender = (
      <div>
        {item.name}
        <span>
          {item.providers.map((i, providerIdx) => (
            <span key={providerIdx.toString()} className={styles.title}>
              (
              {i.name}
              {item.providers.length - 1 === idx ? null : ','}
              {' '}
              )
            </span>
          ))}
        </span>
        <Checkbox
          checked={item.forFeed}
          className={styles.forFeed}
          onChange={(e) => handleChangeForFeed(item._id, e.target.checked)}
        >
          For feed
        </Checkbox>
        <Link
          aria-label='edit'
          className={styles.edit}
          to={APP_ROUTES.PRODUCT_EDIT.replace(':id', item._id)}
        >
          <EditFilled />
        </Link>
        {/* <a */}
        {/*  aria-label='edit' */}
        {/*  className={styles.edit} */}
        {/*  href={APP_ROUTES.PRODUCT_EDIT.replace(':id', item._id)} */}
        {/* > */}
        {/*  <EditFilled /> */}
        {/* </a> */}
      </div>
    );

    return (
      <List.Item className={styles.list} key={idx.toString()}>
        <List.Item.Meta
          avatar={<Avatar src={item.image} />}
          title={titleRender}
          description={descriptionRender}
        />
      </List.Item>
    );
  }, [handleChangeForFeed]);

  return (
    <div>
      <Text type='secondary'>Product list</Text>
      <Input.Search
        placeholder='Search by name...'
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        style={{ marginBottom: 16, marginTop: 8 }}
      />
      <div style={{ marginBottom: 16 }}>
        <Text type='secondary' style={{ marginRight: 8 }}>Sort by price:</Text>
        <Select
          value={priceOrder}
          onChange={handlePriceOrderChange}
          options={ORDER_OPTIONS}
          style={{ marginRight: 16, width: 160 }}
        />
        <Checkbox
          checked={forFeedFilter}
          onChange={(e) => handleForFeedFilterChange(e.target.checked)}
          style={{ marginRight: 16 }}
        >
          Only for feed
        </Checkbox>
        <Checkbox
          checked={inStockFilter}
          onChange={(e) => handleInStockFilterChange(e.target.checked)}
        >
          Only in stock
        </Checkbox>
      </div>
      <List
        className={styles.listWrapper}
        itemLayout='vertical'
        size='large'
        dataSource={data?.data}
        renderItem={renderItem}
        loading={isLoading}
      />
      <Pagination
        className={styles.pagination}
        current={page}
        defaultCurrent={page}
        total={data?.total || 0}
        pageSize={PAGE_SIZE}
        onChange={(p: number) => setPage(p)}
        showSizeChanger={false}
      />
    </div>
  );
};

export default ProductList;
